/**
 * Board Pack - confidential board-only reporting document.
 * Institutional ledger aesthetic per /app/design_guidelines.json:
 * flat white "paper" document on a muted app background, serif
 * headings, mono numbers, horizontal-rule sections, print-friendly
 * (all controls print:hidden). Access enforced server-side via the
 * admin role (403 for everyone else) and client-side via the adminOnly
 * route wrapper.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import {
  Sparkles, Loader2, Plus, X, Lock, Unlock, RefreshCw, Printer,
} from "lucide-react";
import StaffShell from "../components/staff/StaffShell";
import RichTextEditor, { RICH_STYLES, mdliteToHtml, looksLikeHtml } from "../components/quarter/RichTextEditor";
import { SECTION_RENDERERS } from "../components/board/PackSections";

const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { key: "financial", num: "02", title: "Financial performance" },
  { key: "cash", num: "03", title: "Cash and balance sheet" },
  { key: "sales", num: "04", title: "Sales and pipeline" },
  { key: "clients", num: "05", title: "Clients and concentration" },
  { key: "divisions", num: "06", title: "Divisional performance" },
  { key: "risks", num: "07", title: "Risks and integrity" },
  { key: "outlook", num: "08", title: "Outlook" },
];

const RAG = [
  { id: "green", label: "Green", fill: "#166534" },
  { id: "amber", label: "Amber", fill: "#B45309" },
  { id: "red", label: "Red", fill: "#991B1B" },
];

const BOARD_STYLES = `
  .board-doc { font-family: 'IBM Plex Sans', sans-serif; }
  .board-serif { font-family: 'Cormorant Garamond', 'Georgia', serif; }
  .board-doc .qtr-rich { font-size: 0.9rem; color: #374151; line-height: 1.75; }
  .board-doc .board-read { max-width: 68ch; }
  @media print {
    .print-hidden { display: none !important; }
    .board-paper { box-shadow: none !important; border: none !important; }
    section { break-inside: avoid; }
  }
`;

function SectionHeading({ num, title }) {
  return (
    <div className="flex items-baseline gap-4 border-t-2 border-slate-800 pt-4">
      <span className="font-mono text-xs text-[#6B7280] tracking-widest">{num}</span>
      <h2 className="board-serif text-[28px] leading-tight font-semibold text-[#0A192F] tracking-tight">{title}</h2>
    </div>
  );
}

function Narrative({ pack, section, value, onChange, onGenerate, generating }) {
  const readOnly = pack.status === "published";
  const html = looksLikeHtml(value) ? value : mdliteToHtml(value || "");
  if (readOnly) {
    if (!(value || "").trim()) return null;
    return (
      <div className="qtr-rich board-read mt-6" data-testid={`board-read-narrative-${section}`}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
    );
  }
  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-1.5 print-hidden">
        <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#6B7280]">Commentary</p>
        <button
          type="button"
          data-testid={`board-generate-${section}`}
          onClick={onGenerate}
          disabled={generating}
          className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#1F7468] hover:text-[#155a50] disabled:opacity-50"
        >
          {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {generating ? "Writing…" : (value || "").trim() ? "Redraft with AI" : "Draft with AI"}
        </button>
      </div>
      <RichTextEditor
        value={value || ""}
        onChange={onChange}
        placeholder="Chairman's commentary for this section…"
        testid={`board-narrative-${section}`}
        idPrefix={`board-${section}`}
        minHeight="5rem"
      />
    </div>
  );
}

export default function BoardPack() {
  const now = new Date();
  const curQ = Math.floor(now.getMonth() / 3) + 1;
  const [ptype, setPtype] = useState("quarter");
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(curQ);
  const [packs, setPacks] = useState([]);
  const [pack, setPack] = useState(null);
  const [compiling, setCompiling] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/board-pack/list`);
      setPacks(data.packs || []);
      return data.packs || [];
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    loadList().then((ps) => {
      if (ps.length) openPack(ps[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPack = async (pid) => {
    try {
      const { data } = await axios.get(`${API}/api/board-pack/${pid}`);
      setPack(data);
    } catch {
      toast.error("Could not open that pack.");
    }
  };

  const compile = async () => {
    setCompiling(true);
    try {
      const { data } = await axios.post(`${API}/api/board-pack/compile`, {
        period_type: ptype, year, quarter: ptype === "quarter" ? quarter : null,
      });
      setPack(data);
      await loadList();
      toast.success(`${data.label} compiled.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Compile failed.");
    } finally {
      setCompiling(false);
    }
  };

  const setNarrative = (section, html) =>
    setPack((p) => ({ ...p, narrative: { ...(p.narrative || {}), [section]: html } }));

  const save = async () => {
    if (!pack) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/board-pack/${pack.id}`, {
        narrative: pack.narrative || {},
        actions: pack.actions || [],
        rag: pack.rag || null,
      });
      toast.success("Saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const generate = async (section) => {
    if ((pack.narrative?.[section] || "").trim() &&
        !window.confirm("Replace the existing commentary for this section?")) return;
    setGenerating(section);
    try {
      const { data } = await axios.post(`${API}/api/board-pack/${pack.id}/narrative`, { section });
      setNarrative(section, mdliteToHtml(data.text));
      toast.success("Draft ready, edit as needed and save.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed.");
    } finally {
      setGenerating(null);
    }
  };

  const togglePublish = async () => {
    const publishing = pack.status !== "published";
    if (publishing) await save();
    try {
      await axios.post(`${API}/api/board-pack/${pack.id}/${publishing ? "publish" : "unpublish"}`);
      setPack((p) => ({ ...p, status: publishing ? "published" : "draft" }));
      await loadList();
      toast.success(publishing ? "Pack published and frozen." : "Pack reopened for editing.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change status.");
    }
  };

  const addAction = () =>
    setPack((p) => ({
      ...p,
      actions: [...(p.actions || []), { id: `${Date.now()}`, title: "", owner: "", status: "open", added_in: p.label }],
    }));
  const patchAction = (id, patch) =>
    setPack((p) => ({ ...p, actions: (p.actions || []).map((a) => (a.id === id ? { ...a, ...patch } : a)) }));
  const removeAction = (id) =>
    setPack((p) => ({ ...p, actions: (p.actions || []).filter((a) => a.id !== id) }));

  const readOnly = pack?.status === "published";
  const years = [];
  for (let y = now.getFullYear(); y >= 2023; y--) years.push(y);
  const compiledOn = pack ? new Date(pack.compiled_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

  return (
    <StaffShell max="wide">
      <style>{RICH_STYLES}</style>
      <style>{BOARD_STYLES}</style>
      <div data-testid="board-pack-page" className="board-doc -mx-4 sm:-mx-6 lg:-mx-8 -mt-2 bg-[#F3F4F6] min-h-screen px-4 sm:px-6 lg:px-8 pb-16">

        {/* control bar - never printed */}
        <div className="print-hidden sticky top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 bg-white border-b border-slate-200">
          <div className="max-w-6xl mx-auto py-2.5 flex flex-wrap items-center gap-2">
            <span className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#6B7280] mr-1">Board pack</span>
            {packs.map((p) => (
              <button key={p.id} type="button" onClick={() => openPack(p.id)} data-testid={`board-archive-${p.id}`}
                className={[
                  "font-mono text-[11px] px-2.5 py-1 border transition-colors",
                  pack?.id === p.id ? "border-slate-800 bg-slate-900 text-white"
                    : "border-slate-300 bg-white text-[#4B5563] hover:border-slate-500",
                ].join(" ")}>
                {p.label}{p.status === "published" ? " ●" : ""}
              </button>
            ))}
            <span className="flex-1" />
            <select value={ptype} onChange={(e) => setPtype(e.target.value)} data-testid="board-ptype-select"
              className="font-mono text-[11px] border border-slate-300 bg-white px-2 py-1.5">
              <option value="quarter">Quarter</option>
              <option value="year">Full year</option>
            </select>
            {ptype === "quarter" && (
              <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))} data-testid="board-quarter-select"
                className="font-mono text-[11px] border border-slate-300 bg-white px-2 py-1.5">
                {[1, 2, 3, 4].map((q) => <option key={q} value={q}>{`Q${q}`}</option>)}
              </select>
            )}
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="board-year-select"
              className="font-mono text-[11px] border border-slate-300 bg-white px-2 py-1.5">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <button type="button" onClick={compile} disabled={compiling} data-testid="board-compile-btn"
              className="inline-flex items-center gap-1.5 font-mono text-[11px] bg-[#1B4F8A] text-white px-3 py-1.5 hover:bg-[#173f6e] disabled:opacity-50">
              {compiling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {compiling ? "Compiling…" : "Compile"}
            </button>
            {pack && (
              <>
                <button type="button" onClick={() => window.print()} data-testid="board-print-btn" aria-label="Print"
                  className="inline-flex items-center gap-1.5 font-mono text-[11px] border border-slate-300 bg-white px-2.5 py-1.5 hover:border-slate-500">
                  <Printer className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={togglePublish} data-testid="board-publish-btn"
                  className="inline-flex items-center gap-1.5 font-mono text-[11px] border border-slate-300 bg-white px-2.5 py-1.5 hover:border-slate-500">
                  {readOnly ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                  {readOnly ? "Reopen" : "Publish"}
                </button>
                {!readOnly && (
                  <button type="button" onClick={save} disabled={saving} data-testid="board-save-btn"
                    className="font-mono text-[11px] bg-slate-900 text-white px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
                    {saving ? "Saving…" : "Save"}
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {!pack ? (
          <div className="max-w-6xl mx-auto mt-10 board-paper bg-white border border-slate-200 shadow-xl shadow-slate-200/50 p-12 text-center" data-testid="board-empty">
            <p className="font-mono text-sm text-[#6B7280]">No packs yet. Pick a period above and compile the first one.</p>
          </div>
        ) : (
          <article className="max-w-6xl mx-auto mt-8 board-paper bg-white border border-slate-200 shadow-xl shadow-slate-200/50 px-8 md:px-14 lg:px-20 py-12 md:py-16">

            {/* document header */}
            <header>
              <div className="flex items-start justify-between gap-6">
                <div>
                  <p className="font-mono uppercase tracking-[0.3em] text-[10px] text-[#991B1B]">Confidential · Board only</p>
                  <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#6B7280] mt-3">Medicys Ltd</p>
                  <h1 className="board-serif text-5xl md:text-6xl font-semibold text-[#0A192F] tracking-tight mt-1">
                    Board Pack <span className="text-slate-300">·</span> {pack.label}
                  </h1>
                  <p className="font-mono text-[11px] text-[#6B7280] mt-3">
                    Compiled {compiledOn}
                    {pack.status === "published" && pack.published_at
                      ? ` · Published ${(pack.published_at || "").slice(0, 10)}` : " · Draft"}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#6B7280] mb-2">Status</p>
                  <div className="flex gap-1.5 justify-end">
                    {RAG.map((r) => {
                      const active = pack.rag === r.id;
                      return (
                        <button key={r.id} type="button" disabled={readOnly} data-testid={`board-rag-${r.id}`}
                          onClick={() => !readOnly && setPack((p) => ({ ...p, rag: p.rag === r.id ? null : r.id }))}
                          className={`font-mono uppercase tracking-wider text-[10px] px-2.5 py-1.5 border ${readOnly && !active ? "hidden" : ""} ${readOnly ? "cursor-default" : ""}`}
                          style={active
                            ? { background: r.fill, borderColor: r.fill, color: "#fff" }
                            : { background: "#fff", borderColor: "#CBD5E1", color: "#6B7280" }}>
                          <span className="inline-block w-2 h-2 mr-1.5" style={{ background: active ? "#fff" : r.fill }} />
                          {r.label}
                        </button>
                      );
                    })}
                    {readOnly && !pack.rag && <span className="font-mono text-[10px] text-[#6B7280]">Not set</span>}
                  </div>
                </div>
              </div>
              <div className="border-b-2 border-slate-800 mt-8" />
            </header>

            {/* 01 chairman's summary */}
            <section className="mt-10" data-testid="board-section-chair">
              <div className="flex items-baseline gap-4">
                <span className="font-mono text-xs text-[#6B7280] tracking-widest">01</span>
                <h2 className="board-serif text-[28px] leading-tight font-semibold text-[#0A192F] tracking-tight">Chairman's summary</h2>
              </div>
              <Narrative pack={pack} section="chair" value={pack.narrative?.chair}
                onChange={(v) => setNarrative("chair", v)}
                onGenerate={() => generate("chair")} generating={generating === "chair"} />
            </section>

            {/* 02-08 data sections */}
            <div className="mt-12 flex flex-col gap-12">
              {SECTIONS.map(({ key, num, title }) => {
                const Renderer = SECTION_RENDERERS[key];
                const d = (pack.data || {})[key] || { available: false };
                return (
                  <section key={key} data-testid={`board-section-${key}`}>
                    <SectionHeading num={num} title={title} />
                    <Renderer d={d} />
                    <Narrative pack={pack} section={key} value={pack.narrative?.[key]}
                      onChange={(v) => setNarrative(key, v)}
                      onGenerate={() => generate(key)} generating={generating === key} />
                  </section>
                );
              })}
            </div>

            {/* 09 actions */}
            <section className="mt-12" data-testid="board-actions-card">
              <SectionHeading num="09" title="Actions and decisions" />
              {!readOnly && (
                <button type="button" onClick={addAction} data-testid="board-add-action-btn"
                  className="print-hidden mt-4 inline-flex items-center gap-1 font-mono text-[11px] text-[#1B4F8A] hover:text-[#173f6e]">
                  <Plus className="w-3.5 h-3.5" /> Add action
                </button>
              )}
              {(pack.actions || []).length === 0 ? (
                <p className="font-mono text-xs text-[#6B7280] mt-4">No actions recorded. Open items carry forward to the next pack automatically.</p>
              ) : (
                <ol className="mt-4">
                  {(pack.actions || []).map((a, i) => (
                    <li key={a.id} className="py-2.5 border-b border-slate-200 flex items-center gap-4" data-testid={`board-action-${a.id}`}>
                      <span className="font-mono text-[11px] text-[#6B7280] w-6">{String(i + 1).padStart(2, "0")}</span>
                      <button type="button" disabled={readOnly}
                        onClick={() => patchAction(a.id, { status: a.status === "open" ? "done" : "open" })}
                        data-testid={`board-action-toggle-${a.id}`} aria-label="Toggle done"
                        className={`w-3.5 h-3.5 border shrink-0 ${a.status === "done" ? "bg-slate-900 border-slate-900" : "bg-white border-slate-400"}`} />
                      {readOnly ? (
                        <span className={`flex-1 text-sm ${a.status === "done" ? "line-through text-[#6B7280]" : "text-[#0A192F]"}`}>{a.title || "(untitled)"}</span>
                      ) : (
                        <input value={a.title} onChange={(e) => patchAction(a.id, { title: e.target.value })}
                          placeholder="What was agreed…"
                          className={`flex-1 text-sm bg-transparent border-b border-transparent focus:border-slate-300 outline-none py-0.5 ${a.status === "done" ? "line-through text-[#6B7280]" : "text-[#0A192F]"}`} />
                      )}
                      {readOnly ? (
                        <span className="font-mono text-[11px] text-[#6B7280] w-24 truncate text-right">{a.owner}</span>
                      ) : (
                        <input value={a.owner || ""} onChange={(e) => patchAction(a.id, { owner: e.target.value })}
                          placeholder="Owner" className="w-24 font-mono text-[11px] text-[#4B5563] bg-transparent border-b border-transparent focus:border-slate-300 outline-none py-0.5 text-right" />
                      )}
                      {a.carried_from && <span className="font-mono text-[9px] uppercase tracking-wider text-[#6B7280] shrink-0">from {a.carried_from}</span>}
                      {!readOnly && (
                        <button type="button" onClick={() => removeAction(a.id)} aria-label="Remove"
                          className="print-hidden text-slate-300 hover:text-[#991B1B]">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </section>

            <footer className="mt-16 pt-4 border-t-2 border-slate-800 flex items-baseline justify-between">
              <p className="font-mono uppercase tracking-[0.3em] text-[9px] text-[#991B1B]">Confidential · Board only</p>
              <p className="font-mono text-[9px] text-[#6B7280]">Medicys Ltd · {pack.label} · compiled from the live cockpit</p>
            </footer>
          </article>
        )}
      </div>
    </StaffShell>
  );
}
